import { Time } from 'lightweight-charts';

import { EMainProductCodeType } from '@aqx-core/common/api/config';
import { companyService } from '@aqx-core/common/services/company';
import { ICustomBarData } from '@aqx-core/common/services/trade/analysis/equity-chart/types/chart';
import { socketService } from '@aqx-core/common/socket';
import { ISymbolTopicLivePrices } from '@aqx-core/common/socket/subscriptions';
import { ESymbolMessageType, ISymbolTopicMessage } from '@aqx-core/common/socket/topics/symbol';
import { safetyCheckForNumberValues } from '@aqx-core/common/utils/number';

class SymbolTopicSubscription {
  public subscribe = (
    symbol: string,
    subscriptionKey: string,
    updateSymbolPrice: (updatedSymbolId: string, livePrices: ISymbolTopicLivePrices) => void,
    updateChartBarData?: (chartBarData: ICustomBarData) => void
  ) => {
    socketService.topics().symbol.subscribe({
      symbol: symbol,
      receiver: {
        subscriptionKey,
        payload: (payload) => {
          this.symbolTopicReceiver(payload, updateSymbolPrice);
          if (updateChartBarData) {
            this.chartBarDataReceiver(payload, updateChartBarData);
          }
        }
      }
    });
  };

  public unsubscribe = (
    symbol: string,
    subscriptionKey: string,
    updateSymbolPrice: (updatedSymbolId: string, livePrices: ISymbolTopicLivePrices) => void,
    updateChartBarData?: (chartBarData: ICustomBarData) => void
  ) => {
    socketService.topics().symbol.unsubscribe({
      symbol: symbol,
      receiver: {
        subscriptionKey,
        payload: (payload) => {
          this.symbolTopicReceiver(payload, updateSymbolPrice);
          if (updateChartBarData) {
            this.chartBarDataReceiver(payload, updateChartBarData);
          }
        }
      }
    });
  };

  private symbolTopicReceiver = (
    payload: ISymbolTopicMessage | undefined,
    updateSymbolPrice: (updatedSymbolId: string, livePrices: ISymbolTopicLivePrices) => void
  ) => {
    if (payload && payload.M === ESymbolMessageType.LIVE) {
      updateSymbolPrice(payload.D.S, this.massagePayload(payload));
    }
  };

  private chartBarDataReceiver = (
    payload: ISymbolTopicMessage | undefined,
    updateChartBarData: (chartBarData: ICustomBarData) => void
  ) => {
    if (payload && payload.M === ESymbolMessageType.LIVE) {
      updateChartBarData(this.massageChartBarData(payload));
    }
  };

  /** Live Buy = Ask , as we buy at the lowest ask price (lowest sell price user is willing to sell)
   *  Live Sell = Bid , as we sell at the highest bid price (highest buy price user is willing to pay) */
  private massagePayload = (payload: ISymbolTopicMessage) => {
    const updatedData = {
      liveLow: safetyCheckForNumberValues(payload.D.L),
      liveHigh: safetyCheckForNumberValues(payload.D.H),
      liveBuy: safetyCheckForNumberValues(payload.D.A),
      liveSell: safetyCheckForNumberValues(payload.D.B),
      liveSpread: safetyCheckForNumberValues(payload.D.A) - safetyCheckForNumberValues(payload.D.B),
      liveLastPrice: safetyCheckForNumberValues(payload.D.LP)
    };

    return updatedData;
  };

  private massageChartBarData = (payload: ISymbolTopicMessage) => {
    const companyIntegration = companyService.getCompanyIntegration();
    const isHorizon = companyIntegration === EMainProductCodeType.HORIZON;

    const updatedData = {
      open: safetyCheckForNumberValues(
        isHorizon
          ? payload.D.LP // Use last price in MEX
          : payload.D.B // Use Bid for others
      ),
      close: safetyCheckForNumberValues(
        isHorizon
          ? payload.D.LP // Use last price in MEX
          : payload.D.B // Use Bid for others
      ),
      low: safetyCheckForNumberValues(payload.D.L),
      high: safetyCheckForNumberValues(payload.D.H),
      time: (payload.D.T / 1000) as Time,
      ask: safetyCheckForNumberValues(payload.D.A)
    };

    return updatedData;
  };
}

export const symbolTopicSubscription = new SymbolTopicSubscription();
